import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { submitFeedback } from '@/services/feedbackService';
import { useTranslation } from 'react-i18next';

const MAX_MESSAGE_LENGTH = 2000;

const initialState = { type: '', message: '' };
const initialErrors = { type: '', message: '' };

const FeedbackForm = ({ onSuccess }) => {
  const { t } = useTranslation();

  const FEEDBACK_TYPES = [
    { value: 'BUG_REPORT', label: t('feedback.bugreport') },
    { value: 'FEATURE_REQUEST', label: t('feedback.feature') },
    { value: 'GENERAL', label: t('feedback.general') },
    { value: 'OTHER', label: t('feedback.other') },
  ];

  const [values, setValues] = useState(initialState);
  const [errors, setErrors] = useState(initialErrors);
  const [status, setStatus] = useState('idle'); // idle | loading | error

  const validate = () => {
    const next = { ...initialErrors };
    if (!values.type) next.type = t('feedback.select');
    if (!values.message.trim()) next.message = t('feedback.enter');
    setErrors(next);
    return !next.type && !next.message;
  };

  const handleChange = field => (e) => {
    setValues(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setStatus('loading');
    try {
      await submitFeedback(values);
      setStatus('idle');
      setValues(initialState);
      onSuccess?.();
    }
    catch {
      setStatus('error');
    }
  };

  return (
    <Stack spacing={3} sx={{ mt: 1 }}>
      <FormControl fullWidth error={!!errors.type}>
        <InputLabel id="feedback-type-label">{t('feedback.type')}</InputLabel>
        <Select
          labelId="feedback-type-label"
          value={values.type}
          label={t('feedback.type')}
          onChange={handleChange('type')}
        >
          {FEEDBACK_TYPES.map(({ value, label }) => (
            <MenuItem key={value} value={value}>{label}</MenuItem>
          ))}
        </Select>
        {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
      </FormControl>

      <Box>
        <TextField
          fullWidth
          multiline
          minRows={4}
          maxRows={8}
          label={values.type === 'BUG_REPORT' ? t('feedback.describe') : t('feedback.feedback')}
          value={values.message}
          onChange={handleChange('message')}
          error={!!errors.message}
          helperText={errors.message}
          inputProps={{ maxLength: MAX_MESSAGE_LENGTH }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}
        >
          {values.message.length}
          {' '}
          /
          {MAX_MESSAGE_LENGTH}
        </Typography>
      </Box>

      {status === 'error' && (
        <Typography variant="body2" color="error">
          {t('feedback.error')}
        </Typography>
      )}

      <Button
        variant="contained"
        size="large"
        onClick={handleSubmit}
        disabled={status === 'loading'}
        startIcon={status === 'loading' ? <CircularProgress size={16} color="inherit" /> : null}
        fullWidth
      >
        {status === 'loading' ? t('feedback.sending') : t('feedback.send')}
      </Button>
    </Stack>
  );
};

export default FeedbackForm;
